import React, { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { getEobQueueHealth } from "@/lib/eobEraV2";
import { getNoteQueueHealth } from "@/lib/noteClaim";
import { listNoteSplitBatches } from "@/lib/noteClaim";
import { listAnalyses } from "@/lib/patientDocs";
import { listAppointments } from "@/lib/telehealth";
import { useClient } from "@/lib/clientContext";
import { api } from "@/lib/api";
import { relativeTimeAgo } from "@/lib/relativeTime";
import { INDIGO as IND } from "@/lib/tokens";

const maxIso = (list) => (list || []).reduce((m, t) => (t && (!m || new Date(t) > new Date(m)) ? t : m), null);

// Side-menu "Agent activity" row — one nav item (not a card). Routes to the Agent
// activity page and takes the selected style there. Polls note + EOB queue health
// every 10s (paused while the tab is hidden). Counts and times only — no filenames.
function inflightOf(qh) {
  return ((qh?.jobs?.by_tenant_inflight) || []).reduce((s, t) => s + (t.count ?? t.in_flight ?? 1), 0);
}

export function LiveActivityPulse({ collapsed = false, docsBusy = 0 }) {
  const { selectedClientId } = useClient();
  const [note, setNote] = useState(null);
  const [eob, setEob] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [loaded, setLoaded] = useState(false);
  const [docsLive, setDocsLive] = useState(0);
  const [clientLastIso, setClientLastIso] = useState(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    const onBusy = (e) => setDocsLive(Math.max(0, Number(e.detail) || 0));
    window.addEventListener("revq:docs-busy", onBusy);
    return () => window.removeEventListener("revq:docs-busy", onBusy);
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    const load = () => {
      getNoteQueueHealth().then((d) => { if (aliveRef.current) { setNote(d); setLoaded(true); } }).catch(() => {});
      getEobQueueHealth().then((d) => { if (aliveRef.current) setEob(d); }).catch(() => {});
      if (aliveRef.current) setNow(Date.now());
    };
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 10000);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { aliveRef.current = false; clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  // Client-scoped feeds (Lens uploads, multi-part splits, telehealth, dictation)
  // so "last finished" matches the Agent activity page union, not just note/eob.
  useEffect(() => {
    if (!selectedClientId) { setClientLastIso(null); return undefined; }
    let alive = true;
    const load = () => {
      const out = [];
      const done = () => { if (alive) setClientLastIso(maxIso(out.filter(Boolean))); };
      Promise.allSettled([
        listAnalyses({ client_id: selectedClientId, limit: 50, view: "summary", etag: true }).then((d) => out.push(maxIso(((d?.analyses) || []).map((x) => x.created_at)))),
        listNoteSplitBatches({ client_id: selectedClientId, view: "summary", etag: true }).then((b) => out.push(maxIso((b || []).map((x) => x.created_at)))),
        listAppointments(selectedClientId, { etag: true }).then((a) => out.push(maxIso((a || []).map((x) => x.start_time)))),
        api.get("/api/edi/note-to-claim/notes", { params: { client_id: selectedClientId, limit: 100, skip: 0, view: "summary" }, etag: true })
          .then((r) => { const n = r.data?.notes || r.data?.items || []; const dic = n.filter((x) => String(x.visit_source ?? x.source ?? "").toLowerCase().includes("dictation")); out.push(maxIso(dic.map((x) => x.created_at || x.date_of_service))); }),
      ]).then(done);
    };
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [selectedClientId]);

  const codingN = (note?.jobs?.counts?.processing || 0) || inflightOf(note);
  const eobsN = (eob?.jobs?.counts?.processing || 0) || inflightOf(eob);
  const queued = (note?.jobs?.counts?.queued || 0) + (eob?.jobs?.counts?.queued || 0);
  const docsN = Math.max(0, docsBusy || 0, docsLive || 0);
  const working = codingN > 0 || eobsN > 0 || queued > 0 || docsN > 0;
  const ready = loaded || docsN > 0;

  const recent = [
    ...((note?.jobs?.recent_finished) || []),
    ...((eob?.jobs?.recent_finished) || []),
  ].sort((a, b) => new Date(b.finished_at_iso || 0) - new Date(a.finished_at_iso || 0));

  // Line 2: working → agent short name + running count each; idle → last finished.
  let line2 = "";
  if (ready) {
    if (working) {
      const parts = [];
      if (docsN > 0) parts.push(`Lens ${docsN}`);
      if (codingN > 0) parts.push(`Coding ${codingN}`);
      if (eobsN > 0) parts.push(`EOBs ${eobsN}`);
      if (queued > 0) parts.push(`${queued} queued`);
      line2 = `Working · ${parts.join(" · ")}`;
    } else {
      const lastIso = maxIso([recent[0]?.finished_at_iso, clientLastIso]);
      line2 = lastIso ? `Idle · last finished ${relativeTimeAgo(lastIso, now)}` : "Idle";
    }
  }

  const ariaLabel = working ? "Agent activity, working" : "Agent activity, idle";
  const tipText = line2 || "Agent activity";
  const cls = ({ isActive }) => collapsed
    ? `flex-none mx-auto w-[40px] h-[34px] flex items-center justify-center rounded-[8px] transition-colors ${isActive ? "bg-[#141412] text-white" : "hover:bg-[#F4F4F9]"}`
    : `w-full flex items-center gap-[10px] rounded-[9px] mb-1 transition-colors ${isActive ? "bg-[#141412] text-white" : "hover:bg-[#F1F1F4]"}`;

  return (
    <>
      <style>{`
        @keyframes aaRowPulse { 0%{transform:scale(.55);opacity:.4} 70%{transform:scale(1.7);opacity:0} 100%{opacity:0} }
        .aa-row-ring { animation: aaRowPulse 1.8s ease-out infinite; }
        @media (prefers-reduced-motion: reduce) { .aa-row-ring { animation:none; opacity:.25; transform:scale(1.3); } }
      `}</style>
      <NavLink to="/luminaATwork" end className={cls} style={collapsed ? undefined : { padding: "7px 10px" }} data-testid="sidebar-agent-activity" aria-label={collapsed ? `${ariaLabel}. ${tipText}` : ariaLabel} title={collapsed ? tipText : "Agent activity"}>
        {({ isActive }) => {
          const dot = !ready ? "#C4C4C0" : working ? (isActive ? "#fff" : IND) : (isActive ? "rgba(255,255,255,.5)" : "#9B9B97");
          const dotSize = collapsed ? 7 : 8;
          return (
            <>
              <span className="relative inline-flex items-center justify-center flex-none" style={{ width: collapsed ? 22 : 20, height: collapsed ? 22 : 20 }}>
                {!collapsed && ready && working && !isActive ? <span className="aa-row-ring absolute rounded-full" style={{ width: 20, height: 20, background: "rgba(88,86,214,.22)" }} /> : null}
                <img src="/assets/lumina_emblem_black.png" alt="" aria-hidden="true" className="relative" style={{ width: collapsed ? 20 : 18, height: collapsed ? 20 : 18, objectFit: "contain", filter: isActive ? "invert(1)" : "none" }} data-testid="sidebar-agent-emblem" />
                <span className="absolute rounded-full" style={{ width: dotSize, height: dotSize, background: dot, right: -1, bottom: -1, boxShadow: `0 0 0 2px ${isActive ? "#141412" : "#fff"}` }} />
              </span>
              {!collapsed && (
                <span className="min-w-0 flex-1 text-left">
                  <span className="block text-[13.5px] leading-tight truncate" style={{ fontWeight: isActive ? 600 : 500, color: isActive ? "#fff" : "#141412" }} data-testid="sidebar-agent-title">Agent activity</span>
                  {line2 ? <span className="block text-[12px] leading-tight truncate mt-[1px]" style={{ color: isActive ? "rgba(255,255,255,.65)" : "#9A9A96" }} data-testid="sidebar-agent-status">{line2}</span> : null}
                </span>
              )}
            </>
          );
        }}
      </NavLink>
    </>
  );
}

export default LiveActivityPulse;
